import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, Check, BookOpen, User, Phone, Heart, Briefcase, Calendar, Car, Shield, Target, Users, FileText, MessageCircle, ChevronLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ab04b277-779a-46f6-a15c-41b90e7d7254/artifacts/ccdpbqku_TBP-New%20Logo%20Design%20conv%201.png";

const VOLUNTEER_ROLES = [
  {
    name: "Bookshelf Assembler",
    description: "Assemble bookshelves according to instructions. Gather tools and materials, inspect for stability and safety, and report any defects or issues."
  },
  {
    name: "Bookshelf Artisan",
    description: "Personalize bookshelves with paint and creative designs to make them special and meaningful for each child."
  },
  {
    name: "Bookmobile Navigator",
    description: "Safely transport bookshelves to families. Verify delivery schedules, load/unload securely, confirm recipient details, and obtain signed acknowledgments."
  },
  {
    name: "Book Curator",
    description: "Sort and organize donated books by age group and reading level. Curate book bundles for families and maintain inventory."
  },
  {
    name: "Literacy Ambassador",
    description: "Represent the organization at community events, share program information, and engage with the public about the importance of early literacy."
  },
  {
    name: "Event Support",
    description: "Assist with literacy events, book drives, and community outreach activities. Help with setup, registration, and attendee engagement."
  },
  {
    name: "Volunteer Coordinator",
    description: "Recruit, manage, and retain volunteers. Create schedules, serve as primary contact, and support volunteer onboarding and retention."
  },
  {
    name: "Fundraising Support",
    description: "Support fundraising campaigns, donor outreach, and grant research to help sustain and grow our mission."
  },
  {
    name: "Administrative Support",
    description: "Assist with office tasks, data entry, communications, scheduling, and organizational operations."
  },
  {
    name: "Social Media & Marketing",
    description: "Create content, manage social media accounts, and help spread awareness about our mission and impact."
  },
  {
    name: "Photographer / Videographer",
    description: "Capture photos and videos at events and deliveries to document our impact and create promotional materials."
  }
];

const PROFESSIONAL_SKILLS = [
  "Carpentry / Furniture Assembly",
  "Education / Tutoring",
  "Social Media",
  "Event Planning",
  "Graphic Design",
  "Writing / Communications",
  "Data / Technology",
  "Community Outreach",
  "Fundraising"
];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIME_BLOCKS = ["Morning", "Afternoon", "Evening"];
const FREQUENCIES = ["One-time events", "Monthly", "Weekly", "Flexible"];
const REFERRAL_SOURCES = ["Facebook", "Instagram", "TikTok", "Website", "Friend/Family", "Community Event", "Employer", "School", "Other"];
const AGE_GROUPS = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];

const STEPS = [
  { id: 0, title: "Welcome", icon: BookOpen },
  { id: 1, title: "Personal Info", icon: User },
  { id: 2, title: "Emergency Contact", icon: Phone },
  { id: 3, title: "Interests & Roles", icon: Heart },
  { id: 4, title: "Skills", icon: Briefcase },
  { id: 5, title: "Availability", icon: Calendar },
  { id: 6, title: "Transportation", icon: Car },
  { id: 7, title: "Background Check", icon: Shield },
  { id: 8, title: "Motivation", icon: Target },
  { id: 9, title: "Demographics", icon: Users },
  { id: 10, title: "Waivers", icon: FileText },
  { id: 11, title: "Referral", icon: MessageCircle },
  { id: 12, title: "Confirmation", icon: Check },
];

const initialFormData = {
  // Personal Info
  first_name: '',
  last_name: '',
  preferred_name: '',
  email: '',
  phone: '',
  city: '',
  state: '',
  zip_code: '',
  date_of_birth: '',
  pronouns: '',
  
  // Emergency Contact
  emergency_contact: {
    name: '',
    relationship: '',
    phone: '',
    secondary_name: '',
    secondary_relationship: '',
    secondary_phone: ''
  },
  
  // Interests & Roles
  volunteer_roles: [],
  other_skills: '',
  
  // Skills & Experience
  professional_skills: [],
  experience_with_children: false,
  languages_spoken: [],
  
  // Availability
  availability: {
    days: [],
    time_blocks: [],
    frequency: 'Flexible',
    earliest_start_date: ''
  },
  
  // Transportation
  transportation: {
    has_reliable_transportation: false,
    willing_to_drive: false,
    has_valid_license: false,
    comfortable_lifting_40lbs: false
  },
  
  // Background Screening
  background_screening: {
    has_conviction: false,
    conviction_explanation: '',
    willing_to_complete_check: false,
    agrees_to_child_safety: false
  },
  
  // Motivation
  why_volunteer: '',
  literacy_meaning: '',
  impact_hope: '',
  
  // Demographics
  demographics: {
    age_group: '',
    race_ethnicity: '',
    profession: '',
    employer: '',
    corporate_program: false
  },
  
  // Waivers
  waivers: {
    volunteer_agreement: false,
    liability_waiver: false,
    photo_release: false,
    confidentiality_agreement: false,
    code_of_conduct: false,
    digital_signature: '',
    signature_date: ''
  },
  
  // Referral
  referral_source: '',
  referral_other: ''
};

export default function VolunteerApplication() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [languageInput, setLanguageInput] = useState('');

  const progress = ((currentStep) / (STEPS.length - 1)) * 100;

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedFormData = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const toggleNestedArrayItem = (parent, field, item) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: prev[parent][field].includes(item)
          ? prev[parent][field].filter(i => i !== item)
          : [...prev[parent][field], item]
      }
    }));
  };

  const addLanguage = () => {
    if (languageInput.trim() && !formData.languages_spoken.includes(languageInput.trim())) {
      setFormData(prev => ({
        ...prev,
        languages_spoken: [...prev.languages_spoken, languageInput.trim()]
      }));
      setLanguageInput('');
    }
  };

  const removeLanguage = (lang) => {
    setFormData(prev => ({
      ...prev,
      languages_spoken: prev.languages_spoken.filter(l => l !== lang)
    }));
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1: // Personal Info
        if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        return true;
      case 2: // Emergency Contact
        if (!formData.emergency_contact.name || !formData.emergency_contact.relationship || !formData.emergency_contact.phone) {
          toast.error('Please fill in emergency contact information');
          return false;
        }
        return true;
      case 3: // Interests
        if (formData.volunteer_roles.length === 0) {
          toast.error('Please select at least one volunteer role');
          return false;
        }
        return true;
      case 7: // Background Check
        if (!formData.background_screening.willing_to_complete_check || !formData.background_screening.agrees_to_child_safety) {
          toast.error('Please agree to background check and child safety guidelines');
          return false;
        }
        return true;
      case 10: // Waivers
        if (!formData.waivers.volunteer_agreement || !formData.waivers.liability_waiver || 
            !formData.waivers.code_of_conduct || !formData.waivers.digital_signature) {
          toast.error('Please agree to all required waivers and provide your digital signature');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setIsSubmitting(true);
    try {
      // Set signature date
      const submitData = {
        ...formData,
        waivers: {
          ...formData.waivers,
          signature_date: new Date().toISOString().split('T')[0]
        }
      };

      const response = await fetch(`${API}/volunteers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast.success('Application submitted successfully!');
        navigate('/application-confirmation');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeStep />;
      case 1:
        return <PersonalInfoStep formData={formData} updateFormData={updateFormData} />;
      case 2:
        return <EmergencyContactStep formData={formData} updateNestedFormData={updateNestedFormData} />;
      case 3:
        return <InterestsStep formData={formData} toggleArrayItem={toggleArrayItem} updateFormData={updateFormData} />;
      case 4:
        return <SkillsStep 
          formData={formData} 
          toggleArrayItem={toggleArrayItem} 
          updateFormData={updateFormData}
          languageInput={languageInput}
          setLanguageInput={setLanguageInput}
          addLanguage={addLanguage}
          removeLanguage={removeLanguage}
        />;
      case 5:
        return <AvailabilityStep formData={formData} updateNestedFormData={updateNestedFormData} toggleNestedArrayItem={toggleNestedArrayItem} />;
      case 6:
        return <TransportationStep formData={formData} updateNestedFormData={updateNestedFormData} />;
      case 7:
        return <BackgroundCheckStep formData={formData} updateNestedFormData={updateNestedFormData} />;
      case 8:
        return <MotivationStep formData={formData} updateFormData={updateFormData} />;
      case 9:
        return <DemographicsStep formData={formData} updateNestedFormData={updateNestedFormData} />;
      case 10:
        return <WaiversStep formData={formData} updateNestedFormData={updateNestedFormData} />;
      case 11:
        return <ReferralStep formData={formData} updateFormData={updateFormData} />;
      case 12:
        return <ConfirmationStep formData={formData} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="volunteer-application">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#1a3a5c] transition-colors">
              <ChevronLeft className="w-5 h-5" />
              <img src={LOGO_URL} alt="The Bookshelf Project" className="h-10 w-auto" />
            </Link>
            <span className="text-sm text-slate-500">Step {currentStep + 1} of {STEPS.length}</span>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[#1a3a5c]">{STEPS[currentStep].title}</span>
            <span className="text-sm text-slate-500">{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Form Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6 md:p-8">
            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          {currentStep > 0 && (
            <Button 
              variant="outline" 
              onClick={prevStep}
              className="border-2 border-slate-300 text-slate-600 hover:bg-slate-50 rounded-full px-6"
              data-testid="prev-step-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
          )}
          {currentStep === 0 && <div />}
          
          {currentStep < STEPS.length - 1 ? (
            <Button 
              onClick={nextStep}
              className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90 text-white rounded-full px-8"
              data-testid="next-step-btn"
            >
              {currentStep === 0 ? 'Start Application' : 'Continue'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8"
              data-testid="submit-application-btn"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
              <Check className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

// Step Components
function WelcomeStep() {
  return (
    <div className="text-center space-y-6" data-testid="welcome-step">
      <div className="w-20 h-20 bg-[#5cc8e8]/10 rounded-full flex items-center justify-center mx-auto">
        <BookOpen className="w-10 h-10 text-[#5cc8e8]" />
      </div>
      <h1 className="text-3xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
        Welcome to The Bookshelf Project
      </h1>
      <div className="text-left space-y-4 bg-slate-50 rounded-xl p-6 max-w-xl mx-auto">
        <p className="text-slate-600">
          Thank you for your interest in volunteering with The Bookshelf Project. Our volunteers help us build in-home libraries for children across the Washington, DC region by:
        </p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2">
            <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            Assembling bookshelves
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            Delivering books to families
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            Engaging with families
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            Supporting literacy events
          </li>
        </ul>
        <div className="pt-4 border-t border-slate-200">
          <p className="text-sm text-slate-500">
            <strong>Time commitment:</strong> Flexible - from one-time events to regular weekly involvement<br />
            <strong>Age requirement:</strong> 18+ (or youth with guardian approval)
          </p>
        </div>
      </div>
      <p className="text-slate-500 text-sm">
        This application takes approximately 10-15 minutes to complete.
      </p>
    </div>
  );
}

function PersonalInfoStep({ formData, updateFormData }) {
  return (
    <div className="space-y-6" data-testid="personal-info-step">
      <div>
        <h2 className="text-2xl font-bold text-[#1a3a5c] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Personal Information</h2>
        <p className="text-slate-500">Tell us about yourself</p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">First Name *</Label>
          <Input 
            id="first_name" 
            value={formData.first_name} 
            onChange={(e) => updateFormData('first_name', e.target.value)}
            placeholder="John"
            data-testid="first-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name">Last Name *</Label>
          <Input 
            id="last_name" 
            value={formData.last_name} 
            onChange={(e) => updateFormData('last_name', e.target.value)}
            placeholder="Doe"
            data-testid="last-name-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="preferred_name">Preferred Name (optional)</Label>
        <Input 
          id="preferred_name" 
          value={formData.preferred_name} 
          onChange={(e) => updateFormData('preferred_name', e.target.value)}
          placeholder="What should we call you?"
          data-testid="preferred-name-input"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email Address *</Label>
          <Input 
            id="email" 
            type="email"
            value={formData.email} 
            onChange={(e) => updateFormData('email', e.target.value)}
            placeholder="john@example.com"
            data-testid="email-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number *</Label>
          <Input 
            id="phone" 
            type="tel"
            value={formData.phone} 
            onChange={(e) => updateFormData('phone', e.target.value)}
            placeholder="(202) 555-0123"
            data-testid="phone-input"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city">City *</Label>
          <Input 
            id="city" 
            value={formData.city} 
            onChange={(e) => updateFormData('city', e.target.value)}
            placeholder="Washington"
            data-testid="city-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="state">State *</Label>
          <Input 
            id="state" 
            value={formData.state} 
            onChange={(e) => updateFormData('state', e.target.value)}
            placeholder="DC"
            data-testid="state-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="zip_code">ZIP Code *</Label>
          <Input 
            id="zip_code" 
            value={formData.zip_code} 
            onChange={(e) => updateFormData('zip_code', e.target.value)}
            placeholder="20001"
            data-testid="zip-input"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="date_of_birth">Date of Birth *</Label>
          <Input 
            id="date_of_birth" 
            type="date"
            value={formData.date_of_birth} 
            onChange={(e) => updateFormData('date_of_birth', e.target.value)}
            data-testid="dob-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pronouns">Pronouns (optional)</Label>
          <Select value={formData.pronouns} onValueChange={(value) => updateFormData('pronouns', value)}>
            <SelectTrigger data-testid="pronouns-select">
              <SelectValue placeholder="Select pronouns" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="he/him">He/Him</SelectItem>
              <SelectItem value="she/her">She/Her</SelectItem>
              <SelectItem value="they/them">They/Them</SelectItem>
              <SelectItem value="other">Other</SelectItem>
              <SelectItem value="prefer-not">Prefer not to say</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function EmergencyContactStep({ formData, updateNestedFormData }) {
  return (
    <div className="space-y-6" data-testid="emergency-contact-step">
      <div>
        <h2 className="text-2xl font-bold text-[#1a3a5c] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Emergency Contact</h2>
        <p className="text-slate-500">Required for safety purposes</p>
      </div>
      
      <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
        <h3 className="font-semibold text-[#1a3a5c]">Primary Contact</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ec_name">Contact Name *</Label>
            <Input 
              id="ec_name" 
              value={formData.emergency_contact.name} 
              onChange={(e) => updateNestedFormData('emergency_contact', 'name', e.target.value)}
              placeholder="Jane Doe"
              data-testid="ec-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ec_relationship">Relationship *</Label>
            <Input 
              id="ec_relationship" 
              value={formData.emergency_contact.relationship} 
              onChange={(e) => updateNestedFormData('emergency_contact', 'relationship', e.target.value)}
              placeholder="Spouse, Parent, Friend, etc."
              data-testid="ec-relationship-input"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ec_phone">Phone Number *</Label>
          <Input 
            id="ec_phone" 
            type="tel"
            value={formData.emergency_contact.phone} 
            onChange={(e) => updateNestedFormData('emergency_contact', 'phone', e.target.value)}
            placeholder="(202) 555-0123"
            data-testid="ec-phone-input"
          />
        </div>
      </div>

      <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
        <h3 className="font-semibold text-[#1a3a5c]">Secondary Contact (Optional)</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ec2_name">Contact Name</Label>
            <Input 
              id="ec2_name" 
              value={formData.emergency_contact.secondary_name} 
              onChange={(e) => updateNestedFormData('emergency_contact', 'secondary_name', e.target.value)}
              placeholder="Secondary contact name"
              data-testid="ec2-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ec2_relationship">Relationship</Label>
            <Input 
              id="ec2_relationship" 
              value={formData.emergency_contact.secondary_relationship} 
              onChange={(e) => updateNestedFormData('emergency_contact', 'secondary_relationship', e.target.value)}
              placeholder="Relationship"
              data-testid="ec2-relationship-input"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ec2_phone">Phone Number</Label>
          <Input 
            id="ec2_phone" 
            type="tel"
            value={formData.emergency_contact.secondary_phone} 
            onChange={(e) => updateNestedFormData('emergency_contact', 'secondary_phone', e.target.value)}
            placeholder="(202) 555-0123"
            data-testid="ec2-phone-input"
          />
        </div>
      </div>
    </div>
  );
}

function InterestsStep({ formData, toggleArrayItem, updateFormData }) {
  return (
    <div className="space-y-6" data-testid="interests-step">
      <div>
        <h2 className="text-2xl font-bold text-[#1a3a5c] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Volunteer Interests & Roles</h2>
        <p className="text-slate-500">Select all roles you're interested in</p>
      </div>
      
      <div className="space-y-3">
        {VOLUNTEER_ROLES.map((role) => (
          <div 
            key={role.name}
            onClick={() => toggleArrayItem('volunteer_roles', role.name)}
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
              formData.volunteer_roles.includes(role.name) 
                ? 'border-[#5cc8e8] bg-[#5cc8e8]/5' 
                : 'border-slate-200 hover:border-slate-300'
            }`}
            data-testid={`role-${role.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
          >
            <div className="flex items-start gap-3">
              <Checkbox 
                checked={formData.volunteer_roles.includes(role.name)} 
                onCheckedChange={() => toggleArrayItem('volunteer_roles', role.name)}
                className="mt-1"
              />
              <div className="flex-1">
                <span className="text-sm font-semibold text-[#1a3a5c]">{role.name}</span>
                <p className="text-xs text-slate-500 mt-1">{role.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <Label htmlFor="other_skills">Other skills or talents you'd like to offer</Label>
        <Textarea 
          id="other_skills" 
          value={formData.other_skills} 
          onChange={(e) => updateFormData('other_skills', e.target.value)}
          placeholder="Tell us about any other ways you'd like to contribute..."
          rows={3}
          data-testid="other-skills-input"
        />
      </div>
    </div>
  );
}

function SkillsStep({ formData, toggleArrayItem, updateFormData, languageInput, setLanguageInput, addLanguage, removeLanguage }) {
  return (
    <div className="space-y-6" data-testid="skills-step">
      <div>
        <h2 className="text-2xl font-bold text-[#1a3a5c] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Skills & Experience</h2>
        <p className="text-slate-500">Help us understand your background</p>
      </div>
      
      <div className="space-y-4">
        <Label>Professional Skills (select all that apply)</Label>
        <div className="grid md:grid-cols-2 gap-3">
          {PROFESSIONAL_SKILLS.map((skill) => (
            <div 
              key={skill}
              onClick={() => toggleArrayItem('professional_skills', skill)}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                formData.professional_skills.includes(skill) 
                  ? 'border-[#5cc8e8] bg-[#5cc8e8]/5' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`skill-${skill.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            >
              <div className="flex items-center gap-3">
                <Checkbox 
                  checked={formData.professional_skills.includes(skill)} 
                  onCheckedChange={() => toggleArrayItem('professional_skills', skill)}
                />
                <span className="text-sm font-medium text-slate-700">{skill}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-xl">
        <Checkbox 
          id="experience_children"
          checked={formData.experience_with_children} 
          onCheckedChange={(checked) => updateFormData('experience_with_children', checked)}
          data-testid="experience-children-checkbox"
        />
        <Label htmlFor="experience_children" className="cursor-pointer">
          I have experience working with children or families
        </Label>
      </div>

      <div className="space-y-3">
        <Label>Languages Spoken</Label>
        <div className="flex gap-2">
          <Input 
            value={languageInput}
            onChange={(e) => setLanguageInput(e.target.value)}
            placeholder="Add a language..."
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
            data-testid="language-input"
          />
          <Button type="button" onClick={addLanguage} variant="outline" data-testid="add-language-btn">Add</Button>
        </div>
        {formData.languages_spoken.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {formData.languages_spoken.map((lang) => (
              <span 
                key={lang} 
                className="inline-flex items-center gap-1 px-3 py-1 bg-[#5cc8e8]/10 text-[#1a3a5c] rounded-full text-sm"
              >
                {lang}
                <button onClick={() => removeLanguage(lang)} className="ml-1 hover:text-red-500">&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AvailabilityStep({ formData, updateNestedFormData, toggleNestedArrayItem }) {
  return (
    <div className="space-y-6" data-testid="availability-step">
      <div>
        <h2 className="text-2xl font-bold text-[#1a3a5c] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Availability & Commitment</h2>
        <p className="text-slate-500">Let us know when you're available</p>
      </div>
      
      <div className="space-y-4">
        <Label>Days Available</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {DAYS.map((day) => (
            <div 
              key={day}
              onClick={() => toggleNestedArrayItem('availability', 'days', day)}
              className={`p-3 text-center rounded-xl border-2 cursor-pointer transition-all ${
                formData.availability.days.includes(day) 
                  ? 'border-[#5cc8e8] bg-[#5cc8e8]/5 text-[#1a3a5c]' 
                  : 'border-slate-200 hover:border-slate-300 text-slate-600'
              }`}
              data-testid={`day-${day.toLowerCase()}`}
            >
              <span className="text-sm font-medium">{day}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <Label>Time Blocks</Label>
        <div className="grid grid-cols-3 gap-3">
          {TIME_BLOCKS.map((block) => (
            <div 
              key={block}
              onClick={() => toggleNestedArrayItem('availability', 'time_blocks', block)}
              className={`p-4 text-center rounded-xl border-2 cursor-pointer transition-all ${
                formData.availability.time_blocks.includes(block) 
                  ? 'border-[#5cc8e8] bg-[#5cc8e8]/5 text-[#1a3a5c]' 
                  : 'border-slate-200 hover:border-slate-300 text-slate-600'
              }`}
              data-testid={`time-${block.toLowerCase()}`}
            >
              <span className="text-sm font-medium">{block}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>How often can you volunteer?</Label>
        <RadioGroup 
          value={formData.availability.frequency} 
          onValueChange={(value) => updateNestedFormData('availability', 'frequency', value)}
          data-testid="frequency-group"
        >
          {FREQUENCIES.map((freq) => (
            <div key={freq} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-50">
              <RadioGroupItem value={freq} id={freq} />
              <Label htmlFor={freq} className="cursor-pointer">{freq}</Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <Label htmlFor="start_date">Earliest Start Date</Label>
        <Input 
          id="start_date" 
          type="date"
          value={formData.availability.earliest_start_date} 
          onChange={(e) => updateNestedFormData('availability', 'earliest_start_date', e.target.value)}
          data-testid="start-date-input"
        />
      </div>
    </div>
  );
}

function TransportationStep({ formData, updateNestedFormData }) {
  const questions = [
    { key: 'has_reliable_transportation', label: 'Do you have reliable transportation?' },
    { key: 'willing_to_drive', label: 'Are you willing to drive bookshelves or books to families?' },
    { key: 'has_valid_license', label: 'Do you have a valid driver\'s license?' },
    { key: 'comfortable_lifting_40lbs', label: 'Are you comfortable lifting up to 40 lbs?' },
  ];

  return (
    <div className="space-y-6" data-testid="transportation-step">
      <div>
        <h2 className="text-2xl font-bold text-[#1a3a5c] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Transportation & Logistics</h2>
        <p className="text-slate-500">Important for delivery volunteers</p>
      </div>
      
      <div className="space-y-3">
        {questions.map((q) => (
          <div 
            key={q.key}
            className="flex items-center space-x-3 p-4 bg-slate-50 rounded-xl"
          >
            <Checkbox 
              id={q.key}
              checked={formData.transportation[q.key]} 
              onCheckedChange={(checked) => updateNestedFormData('transportation', q.key, checked)}
              data-testid={`transport-${q.key}`}
            />
            <Label htmlFor={q.key} className="cursor-pointer flex-1">{q.label}</Label>
          </div>
        ))}
      </div>
    </div>
  );
}

function BackgroundCheckStep({ formData, updateNestedFormData }) {
  return (
    <div className="space-y-6" data-testid="background-check-step">
      <div>
        <h2 className="text-2xl font-bold text-[#1a3a5c] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Background & Safety Screening</h2>
        <p className="text-slate-500">Required because we work with children</p>
      </div>
      
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <p className="text-sm text-amber-800">
          <strong>Note:</strong> The Bookshelf Project requires background checks for all volunteers who will have direct contact with children and families.
        </p>
      </div>

      <div className="space-y-4">
        <div className="p-4 bg-slate-50 rounded-xl space-y-3">
          <div>
            <Label className="text-base font-medium text-[#1a3a5c]">
              Have you ever been convicted of a crime? *
            </Label>
            <p className="text-xs text-slate-500 mt-1">A conviction does not automatically disqualify you</p>
          </div>
          <RadioGroup 
            value={formData.background_screening.has_conviction ? "yes" : "no"}
            onValueChange={(value) => updateNestedFormData('background_screening', 'has_conviction', value === "yes")}
            className="flex gap-6"
            data-testid="has-conviction-radio"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="conviction-yes" />
              <Label htmlFor="conviction-yes" className="cursor-pointer font-medium">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="conviction-no" />
              <Label htmlFor="conviction-no" className="cursor-pointer font-medium">No</Label>
            </div>
          </RadioGroup>
        </div>

        {formData.background_screening.has_conviction && (
          <div className="space-y-2 pl-4">
            <Label htmlFor="conviction_explanation">Please explain:</Label>
            <Textarea 
              id="conviction_explanation" 
              value={formData.background_screening.conviction_explanation} 
              onChange={(e) => updateNestedFormData('background_screening', 'conviction_explanation', e.target.value)}
              placeholder="Provide details about the conviction..."
              rows={3}
              data-testid="conviction-explanation-input"
            />
          </div>
        )}

        <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-xl">
          <Checkbox 
            id="willing_check"
            checked={formData.background_screening.willing_to_complete_check} 
            onCheckedChange={(checked) => updateNestedFormData('background_screening', 'willing_to_complete_check', checked)}
            data-testid="willing-check-checkbox"
          />
          <Label htmlFor="willing_check" className="cursor-pointer">
            I am willing to complete a background check *
          </Label>
        </div>

        <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-xl">
          <Checkbox 
            id="child_safety"
            checked={formData.background_screening.agrees_to_child_safety} 
            onCheckedChange={(checked) => updateNestedFormData('background_screening', 'agrees_to_child_safety', checked)}
            data-testid="child-safety-checkbox"
          />
          <Label htmlFor="child_safety" className="cursor-pointer">
            I agree to follow child safety guidelines *
          </Label>
        </div>
      </div>
    </div>
  );
}

function MotivationStep({ formData, updateFormData }) {
  return (
    <div className="space-y-6" data-testid="motivation-step">
      <div>
        <h2 className="text-2xl font-bold text-[#1a3a5c] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Motivation & Mission Alignment</h2>
        <p className="text-slate-500">Help us understand why you want to volunteer</p>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="why_volunteer">Why do you want to volunteer with The Bookshelf Project?</Label>
        <Textarea 
          id="why_volunteer" 
          value={formData.why_volunteer} 
          onChange={(e) => updateFormData('why_volunteer', e.target.value)}
          placeholder="Share your motivation..."
          rows={4}
          data-testid="why-volunteer-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="literacy_meaning">What does literacy mean to you?</Label>
        <Textarea 
          id="literacy_meaning" 
          value={formData.literacy_meaning} 
          onChange={(e) => updateFormData('literacy_meaning', e.target.value)}
          placeholder="Share your thoughts on literacy..."
          rows={4}
          data-testid="literacy-meaning-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="impact_hope">What impact do you hope to make through volunteering?</Label>
        <Textarea 
          id="impact_hope" 
          value={formData.impact_hope} 
          onChange={(e) => updateFormData('impact_hope', e.target.value)}
          placeholder="Describe the impact you want to have..."
          rows={4}
          data-testid="impact-hope-input"
        />
      </div>
    </div>
  );
}

function DemographicsStep({ formData, updateNestedFormData }) {
  return (
    <div className="space-y-6" data-testid="demographics-step">
      <div>
        <h2 className="text-2xl font-bold text-[#1a3a5c] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Demographics</h2>
        <p className="text-slate-500">Optional - helps with grant reporting and impact data</p>
      </div>
      
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <p className="text-sm text-blue-800">
          This information is optional and is used only for aggregate reporting to funders. It will not affect your application.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="age_group">Age Group</Label>
          <Select 
            value={formData.demographics?.age_group || ''} 
            onValueChange={(value) => updateNestedFormData('demographics', 'age_group', value)}
          >
            <SelectTrigger data-testid="age-group-select">
              <SelectValue placeholder="Select age group" />
            </SelectTrigger>
            <SelectContent>
              {AGE_GROUPS.map((age) => (
                <SelectItem key={age} value={age}>{age}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="race_ethnicity">Race/Ethnicity</Label>
          <Input 
            id="race_ethnicity" 
            value={formData.demographics?.race_ethnicity || ''} 
            onChange={(e) => updateNestedFormData('demographics', 'race_ethnicity', e.target.value)}
            placeholder="Optional"
            data-testid="race-ethnicity-input"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="profession">Profession</Label>
          <Input 
            id="profession" 
            value={formData.demographics?.profession || ''} 
            onChange={(e) => updateNestedFormData('demographics', 'profession', e.target.value)}
            placeholder="Your profession"
            data-testid="profession-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="employer">Employer</Label>
          <Input 
            id="employer" 
            value={formData.demographics?.employer || ''} 
            onChange={(e) => updateNestedFormData('demographics', 'employer', e.target.value)}
            placeholder="Your employer"
            data-testid="employer-input"
          />
        </div>
      </div>

      <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-xl">
        <Checkbox 
          id="corporate_program"
          checked={formData.demographics?.corporate_program || false} 
          onCheckedChange={(checked) => updateNestedFormData('demographics', 'corporate_program', checked)}
          data-testid="corporate-program-checkbox"
        />
        <Label htmlFor="corporate_program" className="cursor-pointer">
          Are you volunteering through a corporate volunteer program?
        </Label>
      </div>
    </div>
  );
}

function WaiversStep({ formData, updateNestedFormData }) {
  const waivers = [
    { 
      key: 'volunteer_agreement', 
      label: 'Volunteer Agreement', 
      required: true,
      description: 'I agree to perform volunteer services for The Bookshelf Project and understand that my service is provided without compensation or benefits.'
    },
    { 
      key: 'liability_waiver', 
      label: 'Liability Waiver', 
      required: true,
      description: 'I release and hold harmless The Bookshelf Project from any claims, damages, or injuries that may occur during volunteer activities.'
    },
    { 
      key: 'photo_release', 
      label: 'Photo & Media Release', 
      required: false,
      description: 'I grant permission for The Bookshelf Project to use photographs or videos of me for promotional and educational purposes.'
    },
    { 
      key: 'confidentiality_agreement', 
      label: 'Confidentiality Agreement', 
      required: false,
      description: 'I agree to keep confidential all information about families, children, and organizational matters that I learn during my volunteer service.'
    },
    { 
      key: 'code_of_conduct', 
      label: 'Code of Conduct', 
      required: true,
      description: 'I agree to conduct myself professionally, treat all individuals with respect, and follow all organizational policies and procedures.'
    },
  ];

  return (
    <div className="space-y-6" data-testid="waivers-step">
      <div>
        <h2 className="text-2xl font-bold text-[#1a3a5c] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Volunteer Waivers & Agreements</h2>
        <p className="text-slate-500">Please review and agree to the following</p>
      </div>
      
      <div className="p-4 bg-slate-50 rounded-xl space-y-2">
        <p className="text-sm text-slate-600">
          I understand that volunteering with The Bookshelf Project is voluntary and that I agree to follow all policies and procedures.
        </p>
      </div>

      <div className="space-y-4">
        {waivers.map((waiver) => (
          <div key={waiver.key} className="p-4 bg-slate-50 rounded-xl space-y-2">
            <div className="flex items-start space-x-3">
              <Checkbox 
                id={waiver.key}
                checked={formData.waivers[waiver.key]} 
                onCheckedChange={(checked) => updateNestedFormData('waivers', waiver.key, checked)}
                data-testid={`waiver-${waiver.key}`}
                className="mt-0.5"
              />
              <div className="flex-1">
                <Label htmlFor={waiver.key} className="cursor-pointer font-medium text-[#1a3a5c]">
                  {waiver.label} {waiver.required && <span className="text-red-500">*</span>}
                </Label>
                <p className="text-sm text-slate-500 mt-1">{waiver.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <Label htmlFor="digital_signature">Digital Signature *</Label>
        <Input 
          id="digital_signature" 
          value={formData.waivers.digital_signature} 
          onChange={(e) => updateNestedFormData('waivers', 'digital_signature', e.target.value)}
          placeholder="Type your full legal name"
          className="font-serif italic"
          data-testid="digital-signature-input"
        />
        <p className="text-xs text-slate-500">By typing your name above, you acknowledge that this constitutes your electronic signature.</p>
      </div>
    </div>
  );
}

function ReferralStep({ formData, updateFormData }) {
  return (
    <div className="space-y-6" data-testid="referral-step">
      <div>
        <h2 className="text-2xl font-bold text-[#1a3a5c] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>How Did You Hear About Us?</h2>
        <p className="text-slate-500">This helps us with our outreach efforts</p>
      </div>
      
      <div className="space-y-2">
        <Label>Select one:</Label>
        <RadioGroup 
          value={formData.referral_source} 
          onValueChange={(value) => updateFormData('referral_source', value)}
          data-testid="referral-group"
        >
          <div className="grid md:grid-cols-2 gap-2">
            {REFERRAL_SOURCES.map((source) => (
              <div key={source} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200">
                <RadioGroupItem value={source} id={source} />
                <Label htmlFor={source} className="cursor-pointer">{source}</Label>
              </div>
            ))}
          </div>
        </RadioGroup>
      </div>

      {formData.referral_source === 'Other' && (
        <div className="space-y-2">
          <Label htmlFor="referral_other">Please specify:</Label>
          <Input 
            id="referral_other" 
            value={formData.referral_other} 
            onChange={(e) => updateFormData('referral_other', e.target.value)}
            placeholder="How did you hear about us?"
            data-testid="referral-other-input"
          />
        </div>
      )}
    </div>
  );
}

function ConfirmationStep({ formData }) {
  return (
    <div className="space-y-6 text-center" data-testid="confirmation-step">
      <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
        <Check className="w-10 h-10 text-emerald-600" />
      </div>
      
      <div>
        <h2 className="text-2xl font-bold text-[#1a3a5c] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Ready to Submit!</h2>
        <p className="text-slate-500">Please review your information before submitting</p>
      </div>

      <div className="text-left space-y-4 bg-slate-50 rounded-xl p-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-slate-500">Name</p>
            <p className="font-medium text-[#1a3a5c]">{formData.first_name} {formData.last_name}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Email</p>
            <p className="font-medium text-[#1a3a5c]">{formData.email}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Phone</p>
            <p className="font-medium text-[#1a3a5c]">{formData.phone}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Location</p>
            <p className="font-medium text-[#1a3a5c]">{formData.city}, {formData.state} {formData.zip_code}</p>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4">
          <p className="text-sm text-slate-500 mb-2">Selected Roles</p>
          <div className="flex flex-wrap gap-2">
            {formData.volunteer_roles.map((role) => (
              <span key={role} className="px-3 py-1 bg-[#5cc8e8]/10 text-[#1a3a5c] rounded-full text-sm">
                {role}
              </span>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4">
          <p className="text-sm text-slate-500 mb-1">Digital Signature</p>
          <p className="font-serif italic text-[#1a3a5c]">{formData.waivers.digital_signature}</p>
        </div>
      </div>

      <p className="text-sm text-slate-500">
        By clicking "Submit Application", you confirm that all information provided is accurate.
      </p>
    </div>
  );
}
